import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useGetByDevice } from '../hooks/use-responsive';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material';
import { FilePond, registerPlugin } from 'react-filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import type { FilePondFile, ProcessServerConfigFunction } from 'filepond';
import type { FilePond as FilePondInstance } from 'react-filepond';
import Cropper from 'cropperjs';
import { EImageVariant } from '@onesource/schemas';

import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import 'cropperjs/dist/cropper.css';

registerPlugin(
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize,
  FilePondPluginImagePreview,
  FilePondPluginImageExifOrientation,
);

export enum EMimeType {
  JPEG = 'image/jpeg',
  PNG = 'image/png',
  WEBP = 'image/webp',
  PDF = 'application/pdf',
}

/** Predefined format presets for convenience */
export const FILE_FORMATS: Record<string, EMimeType[]> = {
  IMAGE: [EMimeType.JPEG, EMimeType.PNG, EMimeType.WEBP],
  PDF: [EMimeType.PDF],
  IMAGE_AND_PDF: [EMimeType.JPEG, EMimeType.PNG, EMimeType.WEBP, EMimeType.PDF],
};

type TVariantOption = { variant: EImageVariant; label: string };

const VARIANT_OPTIONS: TVariantOption[] = Object.values(EImageVariant).map(variant => ({ variant, label: variant }));

const ALL_VARIANTS = Object.values(EImageVariant);

export type TFileUploaderProps = {
  /**
   * Explicit list of accepted MIME types.
   * Use FILE_FORMATS presets or build a custom list.
   * @example ['image/jpeg', 'image/png']
   * @example FILE_FORMATS.IMAGE_AND_PDF
   */
  allowedFormats: EMimeType[];
  /** Maximum file size. Defaults to '50MB'. */
  maxFileSize?: string;
  /** Maximum number of files allowed */
  maxFiles?: number;
  /** Called with the server ID (object key) after each successful upload */
  onUploaded?: (key: string, filename: string) => void;
  /**
   * Factory that builds a FilePond process handler for the given enabled variants.
   * Preferred over `processHandler` when image variant selection is needed.
   */
  processHandlerFactory?: (enabledVariants: EImageVariant[]) => ProcessServerConfigFunction;
  /** FilePond server process handler — used when processHandlerFactory is not provided */
  processHandler?: ProcessServerConfigFunction;
  /** Label shown in the drop area */
  label?: string;
  /** Override the minimum height of the drop zone (e.g. '240px'). Defaults to FilePond's built-in height. */
  dropzoneHeight?: string;
  /**
   * Initial set of enabled image variants. Defaults to all variants.
   * Only relevant when `processHandlerFactory` is provided and `allowedFormats` includes images.
   */
  imageVariants?: EImageVariant[];
  /**
   * When true the variant checkboxes are interactive (admin mode).
   * When false (default) they are rendered as read-only indicators.
   */
  variantsEditable?: boolean;
  /**
   * Aspect ratio for the crop tool (width / height). e.g. 1 for square, 16/9 for widescreen.
   * Defaults to free-form (NaN).
   */
  cropAspectRatio?: number;
  /** Number of file thumbnails to show per row. Defaults to 1. */
  uploaderColumns?: number;
};

export function FileUploader({
  allowedFormats,
  maxFileSize = '50MB',
  maxFiles = 1,
  onUploaded,
  processHandlerFactory,
  processHandler: processHandlerProp,
  label,
  dropzoneHeight,
  imageVariants = ALL_VARIANTS,
  variantsEditable = false,
  cropAspectRatio = NaN,
  uploaderColumns = 1,
}: TFileUploaderProps) {
  const acceptsImages = allowedFormats.some(f => f.startsWith('image/'));
  const showVariants = acceptsImages && processHandlerFactory !== null;
  const theme = useTheme();
  const { isMobile } = useGetByDevice();
  const uid = useId().replace(/:/g, '');
  const scopeClass = `fp-${uid}`;

  const [enabledVariants, setEnabledVariants] = useState<EImageVariant[]>(imageVariants);

  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState('');
  const [cropperReady, setCropperReady] = useState(false);

  const pondRef = useRef<FilePondInstance | null>(null);
  const cropImageRef = useRef<HTMLImageElement>(null);
  const cropperRef = useRef<Cropper | null>(null);
  const pendingFileIdRef = useRef<string | null>(null);
  const pendingFileNameRef = useRef<string>('cropped.webp');
  const objectUrlRef = useRef<string | null>(null);
  const croppedFileRef = useRef<File | null>(null);
  // Queue of images waiting to be cropped
  const cropQueueRef = useRef<Array<{ file: File; fileId: string }>>([]);
  const cropDialogActiveRef = useRef(false);
  // Guard against duplicate onUploaded calls for the same serverId (e.g. from StrictMode or prop churn)
  const reportedServerIdsRef = useRef<Set<string>>(new Set());

  // Keep a ref to enabledVariants so the process handler can read the latest value
  const enabledVariantsRef = useRef<EImageVariant[]>(enabledVariants);
  useEffect(() => {
    enabledVariantsRef.current = enabledVariants;
  }, [enabledVariants]);

  const processHandler = useMemo(
    () => (processHandlerFactory ? processHandlerFactory(enabledVariants) : processHandlerProp!),
    // Re-build handler whenever enabled variants change (only when factory is provided)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [processHandlerFactory, processHandlerProp, enabledVariants],
  );

  const handleProcessFile = useCallback(
    (_error: unknown, file: FilePondFile) => {
      const key = file.serverId as string;
      if (!key) {
        return;
      }
      if (reportedServerIdsRef.current.has(key)) {
        return;
      }
      reportedServerIdsRef.current.add(key);
      onUploaded?.(key, file.filename);
    },
    [onUploaded],
  );

  const openCropDialog = useCallback((file: File, fileId: string) => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    pendingFileIdRef.current = fileId;
    pendingFileNameRef.current = file.name;
    cropDialogActiveRef.current = true;
    setCropSrc(url);
    setCropperReady(false);
    setCropOpen(true);
  }, []);

  // Open the next queued crop dialog, or do nothing if queue is empty
  const processNextInQueue = useCallback(() => {
    cropDialogActiveRef.current = false;
    const next = cropQueueRef.current.shift();
    if (next) {
      openCropDialog(next.file, next.fileId);
    }
  }, [openCropDialog]);

  // Re-initialize cropper whenever a new image src is set (handles both first open and subsequent images)
  useEffect(() => {
    if (!cropSrc || !cropOpen) {
      return;
    }
    const img = cropImageRef.current;
    if (!img) {
      return;
    }
    if (cropperRef.current) {
      cropperRef.current.destroy();
      cropperRef.current = null;
    }
    setCropperReady(false);
    cropperRef.current = new Cropper(img, {
      viewMode: 1,
      autoCropArea: 0.8,
      aspectRatio: cropAspectRatio,
      movable: true,
      rotatable: true,
      scalable: true,
      zoomable: true,
      responsive: true,
      background: false,
      ready() {
        setCropperReady(true);
      },
    });
    return () => {
      cropperRef.current?.destroy();
      cropperRef.current = null;
    };
  }, [cropSrc, cropOpen, cropAspectRatio]);

  const handleDialogExited = useCallback(() => {
    // If a new crop session already started while the exit animation was playing,
    // don't touch its state — the new session owns objectUrlRef and cropSrc now.
    if (cropDialogActiveRef.current) {
      return;
    }
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setCropSrc('');
    setCropperReady(false);
  }, []);

  const handleCropConfirm = useCallback(() => {
    const cropper = cropperRef.current;
    const pond = pondRef.current;
    const fileId = pendingFileIdRef.current;
    if (!cropper || !pond || !fileId) {
      return;
    }

    const canvas = cropper.getCroppedCanvas({
      maxWidth: 4096,
      maxHeight: 4096,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    });

    canvas.toBlob(
      blob => {
        if (!blob) {
          return;
        }
        const croppedFile = new File([blob], pendingFileNameRef.current.replace(/\.[^.]+$/, '.webp'), {
          type: 'image/webp',
        });
        croppedFileRef.current = croppedFile;
        pond.removeFile(fileId);
        pond.addFile(croppedFile).then(pondFile => {
          pond.processFile(pondFile.id);
          processNextInQueue();
        });
      },
      'image/webp',
      0.85,
    );

    cropper.destroy();
    cropperRef.current = null;
    setCropOpen(false);
  }, [processNextInQueue]);

  const handleCropCancel = useCallback(() => {
    if (cropperRef.current) {
      cropperRef.current.destroy();
      cropperRef.current = null;
    }
    // Upload the original file as-is, then move to next in queue
    const fileId = pendingFileIdRef.current;
    if (fileId) {
      pondRef.current?.processFile(fileId);
    }
    setCropOpen(false);
    processNextInQueue();
  }, [processNextInQueue]);

  const handleCropSkipUpload = useCallback(() => {
    if (cropperRef.current) {
      cropperRef.current.destroy();
      cropperRef.current = null;
    }
    // Remove file from FilePond without uploading
    const fileId = pendingFileIdRef.current;
    if (fileId) {
      pondRef.current?.removeFile(fileId);
    }
    setCropOpen(false);
    processNextInQueue();
  }, [processNextInQueue]);

  const handleVariantToggle = useCallback((variant: EImageVariant) => {
    setEnabledVariants(prev => {
      if (prev.includes(variant)) {
        // Always keep at least one variant enabled
        if (prev.length === 1) {
          return prev;
        }
        return prev.filter(v => v !== variant);
      }
      return [...prev, variant];
    });
  }, []);

  const primaryMain = theme.palette.primary.main;

  const defaultLabel = (() => {
    const hasImages = allowedFormats.some(f => f.startsWith('image/'));
    const hasPdf = allowedFormats.includes(EMimeType.PDF);
    if (hasImages && hasPdf) {
      return 'images or PDFs';
    }
    if (hasImages) {
      return 'images';
    }
    if (hasPdf) {
      return 'PDFs';
    }
    return 'files';
  })();

  return (
    <>
      {showVariants && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Generate sizes
          </Typography>
          <FormGroup row>
            {VARIANT_OPTIONS.map(({ variant, label: variantLabel }) => (
              <FormControlLabel
                key={variant}
                control={
                  <Checkbox
                    size="small"
                    checked={enabledVariants.includes(variant)}
                    onChange={() => handleVariantToggle(variant)}
                    disabled={!variantsEditable}
                  />
                }
                label={<Typography variant="caption">{variantLabel}</Typography>}
                sx={{ mr: 1 }}
              />
            ))}
          </FormGroup>
        </Box>
      )}

      <style>{`
        .${scopeClass} .filepond--root {
          border: 2px dashed ${primaryMain};
          border-radius: 12px;
          overflow: hidden;
          transition: border-color 0.2s, background-color 0.2s;
          ${dropzoneHeight ? `min-height: ${dropzoneHeight};` : ''}
        }
        .${scopeClass} .filepond--root:hover {
          border-color: ${theme.palette.primary.dark};
        }
        .${scopeClass} .filepond--panel-root {
          background-color: ${primaryMain}12;
          border: none;
          box-shadow: none;
        }
        ${
          dropzoneHeight
            ? `
        .${scopeClass} .filepond--drop-label {
          min-height: ${dropzoneHeight};
        }
        `
            : ''
        }
        .${scopeClass} .filepond--drop-label label {
          color: ${theme.palette.primary.dark};
          font-size: 1.3rem;
          font-weight: 500;
        }
        .${scopeClass} .filepond--label-action {
          color: ${primaryMain};
          text-decoration-color: ${primaryMain};
          font-weight: 700;
          font-size: 1.3rem;
        }
        .${scopeClass} .filepond--list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          padding: 8px;
          position: static !important;
          left: unset !important;
          right: unset !important;
          width: 100% !important;
        }
        .${scopeClass} .filepond--item {
          position: static !important;
          transform: none !important;
          width: calc(${100 / uploaderColumns}% - ${((uploaderColumns - 1) * 8) / uploaderColumns}px) !important;
          margin: 0 !important;
        }
        .${scopeClass} .filepond--image-preview-wrapper {
          height: 120px !important;
        }
        .${scopeClass} .filepond--image-preview {
          height: 120px !important;
        }
      `}</style>
      <div className={scopeClass}>
        <FilePond
          ref={pondRef}
          allowMultiple={maxFiles > 1}
          maxFiles={maxFiles}
          acceptedFileTypes={allowedFormats as string[]}
          labelFileTypeNotAllowed={`Only ${defaultLabel} are allowed`}
          maxFileSize={maxFileSize}
          labelMaxFileSizeExceeded={`File is too large (max ${maxFileSize})`}
          styleButtonRemoveItemPosition="left"
          imagePreviewHeight={120}
          labelIdle={label ?? `Drag & drop your ${defaultLabel} or <span class="filepond--label-action">Browse</span>`}
          instantUpload={!acceptsImages}
          onaddfile={(_err, file) => {
            const isPdf = (file.file as File).type === 'application/pdf';
            if (!acceptsImages || isPdf) {
              return;
            }
            // Cropped file re-added after confirm — skip crop queue
            if (croppedFileRef.current && file.file === croppedFileRef.current) {
              croppedFileRef.current = null;
              return;
            }
            // If a crop dialog is already open, queue this file for later
            if (cropDialogActiveRef.current) {
              cropQueueRef.current.push({ file: file.file as File, fileId: file.id });
              return;
            }
            openCropDialog(file.file as File, file.id);
          }}
          server={{ process: processHandler }}
          onprocessfile={handleProcessFile}
          onremovefile={(_err, file) => {
            const key = file.serverId as string | null;
            if (key) {
              reportedServerIdsRef.current.delete(key);
            }
            // Remove from queue if it hasn't been shown yet
            cropQueueRef.current = cropQueueRef.current.filter(q => q.fileId !== file.id);
          }}
          credits={false}
        />
      </div>

      {acceptsImages && (
        <Dialog
          open={cropOpen}
          onClose={(_e, reason) => {
            if (reason !== 'backdropClick') {
              handleCropSkipUpload();
            }
          }}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          keepMounted
          TransitionProps={{ onExited: handleDialogExited }}
        >
          <DialogContent sx={{ p: 0, overflow: 'hidden', bgcolor: '#1a1a1a' }}>
            <Box sx={{ width: '100%', height: isMobile ? '100dvh' : '65vh', position: 'relative' }}>
              {!cropperReady && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                  }}
                >
                  <CircularProgress sx={{ color: '#fff' }} />
                </Box>
              )}
              <img ref={cropImageRef} src={cropSrc} alt="crop preview" style={{ display: 'block', maxWidth: '100%' }} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCropSkipUpload} variant="outlined" color="error">
              Cancel
            </Button>
            <Button onClick={handleCropCancel} variant="outlined">
              Skip Crop
            </Button>
            <Button onClick={handleCropConfirm} variant="contained" disabled={!cropperReady}>
              Apply Crop
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
